/* eslint-disable no-nested-ternary */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment } from 'react';
import { Navigate } from 'react-router-dom';

import {
  AtivoStatus,
  RequiredFile,
} from '../../../../../../api/theHive/redeProto/types';
import {
  IconCloseMS,
  IconExpandMoreMS,
  IconKeyboardArrowRightMS,
  IconSearchMS,
} from '../../../../../../constants/icons';
import {
  AGING_BUCKETS,
  Constants,
} from '../../../../../../constants/redeProto';
import { formatCnpjCpf, formatDate } from '../../../../../../helpers';
import { SectionCard } from '../../components/SectionCard';
import {
  KpiGrid,
  Note,
  ProgressFill,
  ProgressTrack,
  SectionSpacer,
} from '../../styles';
import { heatColor } from '../../theme/tokens';
import { formatInt } from '../../utils/format';
import { AgingChart } from './components/AgingChart';
import { CapacitySection } from './components/CapacitySection';
import { FilterBar } from './components/styles';
import { SummaryKpis } from './components/SummaryKpis';
import { StatusIcon } from './StatusIcon';
import { ClearSearchButton, SearchInput } from './styles';
import { AbandonedFilter, AtivoFilter } from './types';
import useInAccreditation from './useInAccreditation';
import usePoleCapacity from './usePoleCapacity';
import { agingInDays, axisApprovedPct } from './utils';

const TABLE_COLUMNS = 11;

const { aging, countSeparator, countSuffix, filters } =
  Constants.inAccreditation;

const ATIVO_LABEL: Record<AtivoStatus, string> = {
  trial: 'Trial',
  ativo: 'Ativo',
  inativo: 'Inativo',
};

const ATIVO_COLOR: Record<AtivoStatus, 'info' | 'success' | 'default'> = {
  trial: 'info',
  ativo: 'success',
  inativo: 'default',
};

const ATIVO_OPTIONS: { value: AtivoFilter; label: string }[] = [
  { value: 'all', label: filters.allStatus },
  { value: 'ativo', label: ATIVO_LABEL.ativo },
  { value: 'trial', label: ATIVO_LABEL.trial },
  { value: 'inativo', label: ATIVO_LABEL.inativo },
];

const ABANDONED_OPTIONS: { value: AbandonedFilter; label: string }[] = [
  { value: 'all', label: filters.abandonedAll },
  { value: 'only', label: filters.abandonedOnly },
  { value: 'hide', label: filters.abandonedHide },
];

// Collapsed: % of approved documents over the axis total, as a progress bar.
function AxisPercent({ files }: { files: RequiredFile[] }): JSX.Element {
  const pct = axisApprovedPct(files);

  if (pct == null) {
    return <Typography color="text.secondary">—</Typography>;
  }

  const approved = files.filter((f) => f.status === 'approved').length;

  return (
    <Tooltip title={`${approved} de ${files.length} documentos aprovados`}>
      <Box display="flex" alignItems="center" gap={1} minWidth={96}>
        <ProgressTrack height={6} sx={{ minWidth: '64px' }}>
          <ProgressFill pct={pct} color={heatColor(pct)} />
        </ProgressTrack>
        <Typography fontWeight={600} fontSize={12} whiteSpace="nowrap">
          {Math.round(pct)}%
        </Typography>
      </Box>
    </Tooltip>
  );
}

function AxisFiles({ files }: { files: RequiredFile[] }): JSX.Element {
  if (files.length === 0) {
    return (
      <Typography color="text.secondary" fontSize={13}>
        Sem documentos neste eixo.
      </Typography>
    );
  }
  return (
    <Box display="flex" flexDirection="column" gap={0.5}>
      {files.map((file) => (
        <Box key={file.fileName} display="flex" alignItems="center" gap={1}>
          <StatusIcon status={file.status} />
          <Typography fontSize={13}>{file.fileName}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export function InAccreditation(): JSX.Element {
  const {
    role,
    isAllowed,
    items,
    summary,
    truncated,
    filteredCount,
    page,
    setPage,
    totalPages,
    loading,
    expanded,
    toggleExpand,
    order,
    setOrder,
    searchTerm,
    searchDisabled,
    handleSearchChange,
    handleClearSearch,
    ativoFilter,
    setAtivoFilter,
    abandonedFilter,
    setAbandonedFilter,
    agingFilter,
    setAgingFilter,
    hasFilters,
    handleClearFilters,
  } = useInAccreditation();

  const capacity = usePoleCapacity();

  // RBAC: platform admin and bank admin only; others go to Visão Geral.
  if (role !== null && !isAllowed) {
    return <Navigate to="visao-geral" replace />;
  }

  return (
    <Box mt={2}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mb={2}
      >
        <Typography color="text.primary" fontSize={20} fontWeight={600}>
          Em Credenciamento
        </Typography>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
          <SearchInput
            placeholder={Constants.inAccreditation.searchCompany}
            inputProps={{ 'aria-label': 'pesquisar', maxLength: 60 }}
            value={searchTerm}
            disabled={searchDisabled}
            onChange={(e) => handleSearchChange(e.target.value)}
            startAdornment={
              <InputAdornment position="start">{IconSearchMS}</InputAdornment>
            }
            endAdornment={
              searchTerm ? (
                <InputAdornment position="end">
                  <ClearSearchButton
                    aria-label={Constants.inAccreditation.clearSearch}
                    onClick={handleClearSearch}
                  >
                    {IconCloseMS}
                  </ClearSearchButton>
                </InputAdornment>
              ) : null
            }
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={order}
            onChange={(_, next) => next && setOrder(next)}
          >
            <ToggleButton value="fifo" sx={{ textTransform: 'none' }}>
              Mais antigos
            </ToggleButton>
            <ToggleButton value="recent" sx={{ textTransform: 'none' }}>
              Mais recentes
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {Constants.inAccreditation.partialBase}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <KpiGrid>
            <SummaryKpis summary={summary} />
          </KpiGrid>

          <SectionCard
            title={Constants.inAccreditation.aging.title}
            subtitle={Constants.inAccreditation.aging.subtitle}
          >
            <AgingChart buckets={summary.agingBuckets} />
            {summary.noDateCount > 0 && (
              <Note>
                {`${formatInt(summary.noDateCount)} ${aging.noDatePrefix} ${
                  aging.noDateSuffix
                }`}
              </Note>
            )}
          </SectionCard>

          <SectionSpacer>
            <CapacitySection
              poles={capacity.poles}
              ufs={capacity.ufs}
              needsNoticeSelection={capacity.needsNoticeSelection}
              truncated={capacity.truncated}
              loading={capacity.loading}
            />
          </SectionSpacer>

          <SectionSpacer>
            <FilterBar>
              {/* Truncado: filtrar sobre base parcial daria recorte enganoso —
                  a busca server-side cobre o edital inteiro. */}
              <TextField
                select
                size="small"
                disabled={truncated}
                value={ativoFilter}
                onChange={(e) => setAtivoFilter(e.target.value as AtivoFilter)}
                sx={{ minWidth: 170 }}
              >
                {ATIVO_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                disabled={truncated}
                value={agingFilter}
                onChange={(e) => setAgingFilter(e.target.value)}
                sx={{ minWidth: 170 }}
              >
                <MenuItem value="all">{filters.allAging}</MenuItem>
                {AGING_BUCKETS.map((bucket) => (
                  <MenuItem key={bucket.key} value={bucket.key}>
                    {bucket.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                disabled={truncated}
                value={abandonedFilter}
                onChange={(e) =>
                  setAbandonedFilter(e.target.value as AbandonedFilter)
                }
                sx={{ minWidth: 200 }}
              >
                {ABANDONED_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              {hasFilters && !truncated && (
                <Button size="small" onClick={handleClearFilters}>
                  {filters.clear}
                </Button>
              )}

              {/* No modo truncado `summary.total` é só o carregado, então
                  "X de Y" viraria "X de X" e passaria por total do edital. */}
              {!truncated && (
                <Typography
                  fontSize={13}
                  color="text.secondary"
                  sx={{ marginLeft: 'auto' }}
                >
                  {`${formatInt(filteredCount)} ${countSeparator} ` +
                    `${formatInt(summary.total)} ${countSuffix}`}
                </Typography>
              )}
            </FilterBar>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="4%" />
                    <TableCell>Nome da empresa</TableCell>
                    <TableCell align="center">CNPJ</TableCell>
                    <TableCell align="center">Data do pedido</TableCell>
                    <TableCell align="center">{aging.column}</TableCell>
                    <TableCell align="center">Edital</TableCell>
                    <TableCell align="center">Jurídico</TableCell>
                    <TableCell align="center">Engenharia</TableCell>
                    <TableCell align="center">Ativo</TableCell>
                    <TableCell align="center">Pagante</TableCell>
                    <TableCell align="center">Abandonou</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={TABLE_COLUMNS} align="center">
                        {searchTerm || hasFilters
                          ? Constants.inAccreditation.notFound
                          : Constants.inAccreditation.empty}
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item) => {
                      const isOpen = expanded.includes(item.noticeCompanyId);
                      const days = agingInDays(item.requestedAt);
                      return (
                        <Fragment key={item.noticeCompanyId}>
                          <TableRow
                            sx={{ cursor: 'pointer' }}
                            onClick={() => toggleExpand(item.noticeCompanyId)}
                          >
                            <TableCell
                              sx={{ padding: { xs: '8px', xl: '16px' } }}
                            >
                              <IconButton size="small">
                                {isOpen
                                  ? IconExpandMoreMS
                                  : IconKeyboardArrowRightMS}
                              </IconButton>
                            </TableCell>
                            <TableCell>{item.companyName}</TableCell>
                            <TableCell align="center">
                              {formatCnpjCpf(item.cnpj)}
                            </TableCell>
                            <TableCell align="center">
                              {item.requestedAt
                                ? formatDate(item.requestedAt.split('T')[0])
                                : '—'}
                            </TableCell>
                            <TableCell align="center">
                              {days == null ? (
                                <Typography color="text.secondary">
                                  —
                                </Typography>
                              ) : (
                                <Typography fontSize={13}>
                                  {`${formatInt(days)} ${aging.daysShort}`}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {item.noticeName ?? '—'}
                            </TableCell>
                            <TableCell align="center">
                              <AxisPercent files={item.juridico} />
                            </TableCell>
                            <TableCell align="center">
                              <AxisPercent files={item.engenharia} />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                size="small"
                                variant="outlined"
                                color={ATIVO_COLOR[item.ativo]}
                                label={ATIVO_LABEL[item.ativo]}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {item.plano ?? '—'}
                            </TableCell>
                            <TableCell align="center">
                              {item.abandoned ? (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  label="Sim"
                                />
                              ) : (
                                <Typography color="text.secondary">
                                  Não
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              colSpan={TABLE_COLUMNS}
                              sx={{ py: 0, border: 0 }}
                            >
                              <Collapse
                                in={isOpen}
                                unmountOnExit
                                timeout="auto"
                              >
                                <Box
                                  display="flex"
                                  gap={6}
                                  py={2}
                                  px={4}
                                  flexWrap="wrap"
                                >
                                  <Box minWidth={260}>
                                    <Typography fontWeight={600} mb={1}>
                                      Jurídico
                                    </Typography>
                                    <AxisFiles files={item.juridico} />
                                  </Box>
                                  <Box minWidth={260}>
                                    <Typography fontWeight={600} mb={1}>
                                      Engenharia
                                    </Typography>
                                    <AxisFiles files={item.engenharia} />
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  page={page}
                  count={totalPages}
                  onChange={(_, value) => setPage(value)}
                />
              </Box>
            )}
          </SectionSpacer>
        </>
      )}
    </Box>
  );
}
